import { useResourceRoutePreview } from '@/shared/components/resource-route-preview/context';
import { PERMISSIONS, PermissionTuple } from '@/shared/permissions';

import { getAccessState, useCASL } from './useCASL';

export type WorkspaceResource =
    'folders' | 'investments' | 'memos' | 'pipelines' | 'dashboards' | 'lists' | 'lifecycles';

export type WorkspaceResourceAction = 'read' | 'update' | 'comment' | 'manage' | 'invite';

type ResourcePermissions = Record<WorkspaceResourceAction, PermissionTuple>;

export const workspaceResourcePermissions: Record<WorkspaceResource, ResourcePermissions> = {
    folders: {
        read: PERMISSIONS.FOLDER.READ,
        update: PERMISSIONS.FOLDER.UPDATE,
        comment: PERMISSIONS.FOLDER.COMMENT,
        manage: PERMISSIONS.FOLDER.MANAGE,
        invite: PERMISSIONS.FOLDER.INVITE,
    },
    investments: {
        read: PERMISSIONS.INVESTMENT.READ,
        update: PERMISSIONS.INVESTMENT.UPDATE,
        comment: PERMISSIONS.INVESTMENT.COMMENT,
        manage: PERMISSIONS.INVESTMENT.MANAGE,
        invite: PERMISSIONS.INVESTMENT.INVITE,
    },
    memos: {
        read: PERMISSIONS.MEMO.READ,
        update: PERMISSIONS.MEMO.UPDATE,
        comment: PERMISSIONS.MEMO.COMMENT,
        manage: PERMISSIONS.MEMO.MANAGE,
        invite: PERMISSIONS.MEMO.INVITE,
    },
    pipelines: {
        read: PERMISSIONS.PIPELINE.READ,
        update: PERMISSIONS.PIPELINE.UPDATE,
        comment: PERMISSIONS.PIPELINE.COMMENT,
        manage: PERMISSIONS.PIPELINE.MANAGE,
        invite: PERMISSIONS.PIPELINE.INVITE,
    },
    dashboards: {
        read: PERMISSIONS.DASHBOARD.READ,
        update: PERMISSIONS.DASHBOARD.UPDATE,
        comment: PERMISSIONS.DASHBOARD.COMMENT,
        manage: PERMISSIONS.DASHBOARD.MANAGE,
        invite: PERMISSIONS.DASHBOARD.INVITE,
    },
    lists: {
        read: PERMISSIONS.LIST.READ,
        update: PERMISSIONS.LIST.UPDATE,
        comment: PERMISSIONS.LIST.COMMENT,
        manage: PERMISSIONS.LIST.MANAGE,
        invite: PERMISSIONS.LIST.INVITE,
    },
    lifecycles: {
        read: PERMISSIONS.LIFECYCLE.READ,
        update: PERMISSIONS.LIFECYCLE.UPDATE,
        comment: PERMISSIONS.LIFECYCLE.COMMENT,
        manage: PERMISSIONS.LIFECYCLE.MANAGE,
        invite: PERMISSIONS.LIFECYCLE.INVITE,
    },
};

const deniedAccessState = {
    allowed: false,
    hidden: true,
    disabled: true,
    readOnly: true,
    canRead: false,
    canInteract: false,
    canMutate: false,
    reason: 'RESOURCE_NOT_LOADED',
} as const;

const readOnlyPreviewAccessState = {
    allowed: false,
    hidden: true,
    disabled: true,
    readOnly: true,
    canRead: true,
    canInteract: false,
    canMutate: false,
    reason: 'READ_ONLY_RESOURCE_PREVIEW',
} as const;

export const getWorkspaceResourcePermission = (resource: WorkspaceResource, action: WorkspaceResourceAction) =>
    workspaceResourcePermissions[resource][action];

export const useWorkspaceResourceAccess = (resource: WorkspaceResource, resourceId?: string | null) => {
    const { ability } = useCASL();
    const preview = useResourceRoutePreview();
    // All resources rendered inside the portal (including child rows/cards) are read-only.
    const isReadOnlyPreview = Boolean(preview);

    const accessState = (action: WorkspaceResourceAction) => {
        if (!resourceId) {
            return deniedAccessState;
        }

        if (isReadOnlyPreview && action !== 'read') {
            return readOnlyPreviewAccessState;
        }

        return getAccessState(ability, getWorkspaceResourcePermission(resource, action), {
            instance: { id: resourceId },
        });
    };

    return {
        read: accessState('read'),
        update: accessState('update'),
        comment: accessState('comment'),
        manage: accessState('manage'),
        invite: accessState('invite'),
        canRead: accessState('read').allowed,
        canUpdate: accessState('update').allowed,
        canComment: accessState('comment').allowed,
        canManage: accessState('manage').allowed,
        canInvite: accessState('invite').allowed,
    };
};
